import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, BookOpen, CheckCircle2, FileEdit, Layers, FlaskConical } from 'lucide-react';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import CanAccess from '@/components/ui/CanAccess';
import EmptyState from '@/components/ui/EmptyState';
import { format } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SpecParameter {
  id: string;
  name: string;
  unit: string;
  minValue: number | null;
  maxValue: number | null;
  targetValue: number | null;
}

interface Spec {
  id: string;
  specCode: string;
  product: string;
  version: string;
  matrix: string;
  description?: string;
  methodRef?: string;
  status: string;
  approvedBy?: string;
  parameters: SpecParameter[];
  createdAt: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_SPECS: Spec[] = [
  // ── Existing 4 records ──────────────────────────────────────────────────────
  {
    id: '1', specCode: 'SPEC-001', product: 'Amoxicillin 500mg', version: 'v2.1', matrix: 'Tablet',
    description: 'Finished product specification for Amoxicillin capsules', methodRef: 'USP-2024-AMX',
    status: 'active', approvedBy: 'Dr. Sharma', createdAt: '2024-01-15T10:00:00Z',
    parameters: [
      { id: 'p1', name: 'Assay', unit: '%', minValue: 90, maxValue: 110, targetValue: 100 },
      { id: 'p2', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null, targetValue: 85 },
      { id: 'p3', name: 'Water Content', unit: '%', minValue: null, maxValue: 5, targetValue: 3 },
    ],
  },
  {
    id: '2', specCode: 'SPEC-002', product: 'Paracetamol 650mg', version: 'v1.4', matrix: 'Tablet',
    methodRef: 'BP-2024-PCT', status: 'draft', createdAt: '2024-02-10T08:00:00Z',
    parameters: [
      { id: 'p4', name: 'Assay', unit: '%', minValue: 95, maxValue: 105, targetValue: 100 },
      { id: 'p5', name: 'Hardness', unit: 'kP', minValue: 5, maxValue: 20, targetValue: 12 },
    ],
  },
  {
    id: '3', specCode: 'SPEC-003', product: 'Metformin 500mg', version: 'v3.0', matrix: 'Tablet',
    methodRef: 'USP-2024-MET', status: 'active', approvedBy: 'QA Director', createdAt: '2023-11-20T12:00:00Z',
    parameters: [
      { id: 'p6', name: 'Assay', unit: '%', minValue: 93, maxValue: 107, targetValue: 100 },
      { id: 'p7', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.5, targetValue: 0.1 },
    ],
  },
  {
    id: '4', specCode: 'SPEC-004', product: 'Ibuprofen 400mg', version: 'v1.0', matrix: 'Tablet',
    status: 'superseded', approvedBy: 'Dr. Kapoor', createdAt: '2022-06-01T09:00:00Z',
    parameters: [],
  },

  // ── New records ─────────────────────────────────────────────────────────────
  {
    id: '5', specCode: 'SPEC-005', product: 'Ibuprofen 400mg', version: 'v2.0', matrix: 'Tablet',
    description: 'Revised specification with updated dissolution criteria per ICH Q6A', methodRef: 'IP-2023-IBU',
    status: 'active', approvedBy: 'Dr. Kapoor', createdAt: '2023-03-15T09:00:00Z',
    parameters: [
      { id: 'p8', name: 'Assay', unit: '%', minValue: 95, maxValue: 105, targetValue: 100 },
      { id: 'p9', name: 'Dissolution', unit: '%', minValue: 70, maxValue: null, targetValue: 80 },
      { id: 'p10', name: 'Hardness', unit: 'kP', minValue: 6, maxValue: 18, targetValue: 12 },
      { id: 'p11', name: 'Disintegration', unit: 'min', minValue: null, maxValue: 30, targetValue: 15 },
    ],
  },
  {
    id: '6', specCode: 'SPEC-006', product: 'Atorvastatin 10mg', version: 'v1.2', matrix: 'Tablet',
    description: 'Finished product specification for Atorvastatin 10mg film-coated tablets', methodRef: 'USP-2023-ATV',
    status: 'active', approvedBy: 'Dr. Sharma', createdAt: '2023-05-20T10:00:00Z',
    parameters: [
      { id: 'p12', name: 'Assay', unit: '%', minValue: 90, maxValue: 110, targetValue: 100 },
      { id: 'p13', name: 'Dissolution', unit: '%', minValue: 80, maxValue: null, targetValue: 90 },
      { id: 'p14', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.3, targetValue: 0.1 },
      { id: 'p15', name: 'Water Content', unit: '%', minValue: null, maxValue: 4, targetValue: 2 },
    ],
  },
  {
    id: '7', specCode: 'SPEC-007', product: 'Ciprofloxacin 500mg', version: 'v2.0', matrix: 'Tablet',
    description: 'Specification for Ciprofloxacin HCl 500mg tablets', methodRef: 'BP-2023-CIP',
    status: 'active', approvedBy: 'Dr. Mehta', createdAt: '2023-07-10T08:00:00Z',
    parameters: [
      { id: 'p16', name: 'Assay', unit: '%', minValue: 95, maxValue: 105, targetValue: 100 },
      { id: 'p17', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null, targetValue: 85 },
      { id: 'p18', name: 'Heavy Metals', unit: 'ppm', minValue: null, maxValue: 10, targetValue: null },
    ],
  },
  {
    id: '8', specCode: 'SPEC-008', product: 'Ondansetron 4mg Injection', version: 'v1.0', matrix: 'Injection',
    description: 'Sterile injectable specification for Ondansetron 4mg/2mL', methodRef: 'USP-2023-OND',
    status: 'active', approvedBy: 'Dr. Pillai', createdAt: '2023-09-01T09:00:00Z',
    parameters: [
      { id: 'p19', name: 'Assay', unit: '%', minValue: 90, maxValue: 110, targetValue: 100 },
      { id: 'p20', name: 'pH', unit: '', minValue: 3.3, maxValue: 4.0, targetValue: 3.7 },
      { id: 'p21', name: 'Particulate Matter', unit: 'particles/mL', minValue: null, maxValue: 10, targetValue: null },
      { id: 'p22', name: 'Sterility', unit: '', minValue: null, maxValue: null, targetValue: null },
    ],
  },
  {
    id: '9', specCode: 'SPEC-009', product: 'Amoxicillin 500mg API', version: 'v3.1', matrix: 'API',
    description: 'Raw material specification for Amoxicillin trihydrate API', methodRef: 'ICH-Q6A-AMX-API',
    status: 'active', approvedBy: 'Dr. Sharma', createdAt: '2023-10-15T11:00:00Z',
    parameters: [
      { id: 'p23', name: 'Assay', unit: '%', minValue: 96, maxValue: 102, targetValue: 99 },
      { id: 'p24', name: 'Particle Size (D90)', unit: 'µm', minValue: null, maxValue: 150, targetValue: 100 },
      { id: 'p25', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.2, targetValue: 0.05 },
      { id: 'p26', name: 'Heavy Metals', unit: 'ppm', minValue: null, maxValue: 20, targetValue: null },
      { id: 'p27', name: 'Water Content', unit: '%', minValue: 11.5, maxValue: 14.5, targetValue: 13 },
    ],
  },
  {
    id: '10', specCode: 'SPEC-010', product: 'Amoxicillin 500mg', version: 'v1.0', matrix: 'Capsule',
    description: 'Original capsule specification — superseded by SPEC-001', methodRef: 'USP-2022-AMX',
    status: 'superseded', approvedBy: 'Dr. Sharma', createdAt: '2022-04-01T09:00:00Z',
    parameters: [
      { id: 'p28', name: 'Assay', unit: '%', minValue: 88, maxValue: 112, targetValue: 100 },
      { id: 'p29', name: 'Dissolution', unit: '%', minValue: 70, maxValue: null, targetValue: 80 },
    ],
  },
  {
    id: '11', specCode: 'SPEC-011', product: 'Metformin 500mg', version: 'v2.0', matrix: 'Tablet',
    description: 'Previous version — superseded by SPEC-003 after ICH Q1A review', methodRef: 'USP-2022-MET',
    status: 'archived', approvedBy: 'QA Director', createdAt: '2022-08-20T10:00:00Z',
    parameters: [
      { id: 'p30', name: 'Assay', unit: '%', minValue: 90, maxValue: 110, targetValue: 100 },
    ],
  },
  {
    id: '12', specCode: 'SPEC-012', product: 'Paracetamol 500mg Liquid', version: 'v1.0', matrix: 'Tablet',
    description: 'Oral liquid specification for paediatric use', methodRef: 'IP-2024-PCT-LIQ',
    status: 'draft', createdAt: '2024-06-01T08:00:00Z',
    parameters: [
      { id: 'p31', name: 'Assay', unit: '%', minValue: 90, maxValue: 110, targetValue: 100 },
      { id: 'p32', name: 'pH', unit: '', minValue: 3.8, maxValue: 5.5, targetValue: 4.5 },
      { id: 'p33', name: 'Viscosity', unit: 'mPa·s', minValue: 20, maxValue: 80, targetValue: 50 },
    ],
  },
  {
    id: '13', specCode: 'SPEC-013', product: 'Levofloxacin 500mg', version: 'v1.1', matrix: 'Tablet',
    description: 'Finished product specification for Levofloxacin 500mg film-coated tablets', methodRef: 'USP-2024-LEV',
    status: 'active', approvedBy: 'Dr. Mehta', createdAt: '2024-08-05T09:00:00Z',
    parameters: [
      { id: 'p34', name: 'Assay', unit: '%', minValue: 95, maxValue: 105, targetValue: 100 },
      { id: 'p35', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null, targetValue: 85 },
      { id: 'p36', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.5, targetValue: 0.1 },
      { id: 'p37', name: 'Hardness', unit: 'kP', minValue: 8, maxValue: 22, targetValue: 15 },
    ],
  },
  {
    id: '14', specCode: 'SPEC-014', product: 'Meropenem 1g Injection', version: 'v2.0', matrix: 'Injection',
    description: 'Lyophilised injectable specification per EU GMP Annex 1', methodRef: 'BP-2025-MER',
    status: 'active', approvedBy: 'Dr. Pillai', createdAt: '2025-01-10T10:00:00Z',
    parameters: [
      { id: 'p38', name: 'Assay', unit: '%', minValue: 90, maxValue: 110, targetValue: 100 },
      { id: 'p39', name: 'Reconstituted pH', unit: '', minValue: 7.0, maxValue: 8.5, targetValue: 7.8 },
      { id: 'p40', name: 'Particulate Matter', unit: 'particles/mL', minValue: null, maxValue: 6, targetValue: null },
      { id: 'p41', name: 'Water Content (KF)', unit: '%', minValue: null, maxValue: 2.5, targetValue: 1.5 },
      { id: 'p42', name: 'Sterility', unit: '', minValue: null, maxValue: null, targetValue: null },
    ],
  },
  {
    id: '15', specCode: 'SPEC-015', product: 'Doxycycline 100mg', version: 'v1.3', matrix: 'Capsule',
    description: 'Specification for Doxycycline Hyclate 100mg hard gelatin capsules', methodRef: 'USP-2025-DOX',
    status: 'draft', createdAt: '2025-11-20T09:00:00Z',
    parameters: [
      { id: 'p43', name: 'Assay', unit: '%', minValue: 90, maxValue: 110, targetValue: 100 },
      { id: 'p44', name: 'Dissolution', unit: '%', minValue: 75, maxValue: null, targetValue: 85 },
      { id: 'p45', name: 'Water Content', unit: '%', minValue: null, maxValue: 6, targetValue: 4 },
      { id: 'p46', name: 'Related Substances', unit: '%', minValue: null, maxValue: 0.4, targetValue: 0.1 },
    ],
  },
];

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number; icon: React.ElementType; color: string }) {
  return (
    <div className="card p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold font-serif text-text">{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Specifications() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedSpec, setSelectedSpec] = useState<Spec | null>(null);
  const [showAddParam, setShowAddParam] = useState(false);

  const [form, setForm] = useState({
    product: '', version: '', matrix: '', description: '', methodRef: '',
  });
  const [paramForm, setParamForm] = useState({
    name: '', unit: '', minValue: '', maxValue: '', targetValue: '',
  });

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['lims-specs', { search, status: statusFilter, page }],
    queryFn: () =>
      api.get('/lims/specs', { params: { search, status: statusFilter, page, limit: 20 } })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_SPECS, meta: { total: MOCK_SPECS.length } })),
  });

  const rows: Spec[] = (Array.isArray(data?.data) && data.data.length > 0) ? data.data : MOCK_SPECS;
  const total: number = data?.meta?.total ?? rows.length;

  const stats = {
    total: rows.length,
    active: rows.filter((s) => s.status === 'active').length,
    draft: rows.filter((s) => s.status === 'draft').length,
    params: rows.reduce((acc, s) => acc + (Array.isArray(s.parameters) ? s.parameters.length : 0), 0),
  };

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/lims/specs', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lims-specs'] });
      setShowCreate(false);
      setForm({ product: '', version: '', matrix: '', description: '', methodRef: '' });
    },
  });

  const addParamMutation = useMutation({
    mutationFn: (payload: { specId: string; param: typeof paramForm }) =>
      api.post(`/lims/specs/${payload.specId}/parameters`, payload.param),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lims-specs'] });
      setShowAddParam(false);
      setParamForm({ name: '', unit: '', minValue: '', maxValue: '', targetValue: '' });
    },
  });

  const columns: Column<Record<string, unknown>>[] = [
    {
      key: 'specCode', label: 'Spec Code', width: 'w-32',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.specCode)}</span>,
    },
    {
      key: 'product', label: 'Product',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm">{String(row.product)}</p>
          <p className="text-xs text-text-dim">{String(row.matrix)}</p>
        </div>
      ),
    },
    {
      key: 'version', label: 'Version', width: 'w-20',
      render: (row) => <span className="text-xs font-mono text-text">{String(row.version)}</span>,
    },
    {
      key: 'status', label: 'Status', width: 'w-28',
      render: (row) => <PillBadge value={String(row.status)} showDot />,
    },
    {
      key: 'parameters', label: 'Parameters', width: 'w-28',
      render: (row) => {
        const params = row.parameters as SpecParameter[];
        return (
          <span className="text-sm font-semibold text-text">
            {Array.isArray(params) ? params.length : 0}
          </span>
        );
      },
    },
    {
      key: 'approvedBy', label: 'Approved By',
      render: (row) => (
        <span className="text-xs text-text-muted">{row.approvedBy ? String(row.approvedBy) : '—'}</span>
      ),
    },
    {
      key: 'createdAt', label: 'Created',
      render: (row) => (
        <span className="text-xs font-mono text-text-dim">
          {row.createdAt ? format(new Date(String(row.createdAt)), 'd MMM yyyy') : '—'}
        </span>
      ),
    },
  ];

  const drawerParams: SpecParameter[] = Array.isArray(selectedSpec?.parameters) ? selectedSpec!.parameters : [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Specification Library"
        subtitle={`${total} specifications managed`}
        actions={
          <CanAccess roles={['admin', 'qa_director']}>
            <button onClick={() => setShowCreate(true)} className="btn-primary">
              <Plus size={13} /> Add Spec
            </button>
          </CanAccess>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Specs" value={stats.total} icon={BookOpen} color="#b07d1a" />
        <StatCard label="Active" value={stats.active} icon={CheckCircle2} color="#16A34A" />
        <StatCard label="Draft" value={stats.draft} icon={FileEdit} color="#6B7280" />
        <StatCard label="Parameters Defined" value={stats.params} icon={Layers} color="#2563EB" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input
            placeholder="Search by product or spec code..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input pl-8"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="select w-36"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="superseded">Superseded</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={rows as unknown as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedSpec(row as unknown as Spec)}
        total={total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* Spec Detail SideDrawer */}
      <SideDrawer
        isOpen={!!selectedSpec}
        onClose={() => setSelectedSpec(null)}
        title={selectedSpec?.product || ''}
        subtitle={`${selectedSpec?.specCode} · ${selectedSpec?.version}`}
        width={560}
        footer={
          <CanAccess roles={['admin', 'qa_director']}>
            <button onClick={() => setShowAddParam(true)} className="btn-primary">
              <Plus size={13} /> Add Parameter
            </button>
          </CanAccess>
        }
      >
        {selectedSpec && (
          <div className="space-y-5">
            {/* Metadata */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Status', value: <PillBadge value={selectedSpec.status} showDot /> },
                { label: 'Matrix', value: selectedSpec.matrix },
                { label: 'Version', value: selectedSpec.version },
                { label: 'Method Ref', value: selectedSpec.methodRef || '—' },
                { label: 'Approved By', value: selectedSpec.approvedBy || '—' },
                { label: 'Created', value: format(new Date(selectedSpec.createdAt), 'd MMM yyyy') },
              ].map(({ label, value }) => (
                <div key={label} className="p-3 bg-bg rounded-lg">
                  <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">{label}</p>
                  <div className="text-xs font-medium text-text">{typeof value === 'string' ? value : value}</div>
                </div>
              ))}
            </div>

            {selectedSpec.description && (
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Description</p>
                <p className="text-sm text-text leading-relaxed">{selectedSpec.description}</p>
              </div>
            )}

            {/* Parameters Table */}
            <div>
              <p className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1.5">
                <FlaskConical size={12} className="text-teal" /> Parameters ({drawerParams.length})
              </p>
              {drawerParams.length === 0 ? (
                <EmptyState icon="data" title="No parameters defined" description="Add parameters to define acceptance criteria." />
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-bg border-b border-border">
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">Parameter</th>
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">Unit</th>
                        <th className="text-right px-3 py-2 text-text-dim font-semibold">Min</th>
                        <th className="text-right px-3 py-2 text-text-dim font-semibold">Target</th>
                        <th className="text-right px-3 py-2 text-text-dim font-semibold">Max</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawerParams.map((p, idx) => (
                        <tr key={p.id} className={idx % 2 === 0 ? 'bg-card' : 'bg-bg'}>
                          <td className="px-3 py-2 font-medium text-text">{p.name}</td>
                          <td className="px-3 py-2 text-text-muted">{p.unit}</td>
                          <td className="px-3 py-2 text-right font-mono text-text-muted">
                            {p.minValue !== null ? (Number(p.minValue) || 0).toFixed(2) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-teal font-semibold">
                            {p.targetValue !== null ? (Number(p.targetValue) || 0).toFixed(2) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-text-muted">
                            {p.maxValue !== null ? (Number(p.maxValue) || 0).toFixed(2) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </SideDrawer>

      {/* Create Spec Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create Specification"
        subtitle="Add a new product specification to the library"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button
              onClick={() => createMutation.mutate(form)}
              disabled={createMutation.isPending || !form.product}
              className="btn-primary"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Specification'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Product *</label>
            <input
              className="input"
              value={form.product}
              onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
              placeholder="e.g. Amoxicillin 500mg Capsules"
            />
          </div>
          <div>
            <label className="label">Version</label>
            <input
              className="input"
              value={form.version}
              onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
              placeholder="e.g. v1.0"
            />
          </div>
          <div>
            <label className="label">Matrix</label>
            <select
              className="select"
              value={form.matrix}
              onChange={(e) => setForm((f) => ({ ...f, matrix: e.target.value }))}
            >
              <option value="">Select matrix...</option>
              {['Tablet', 'Capsule', 'Liquid', 'Injection', 'Cream', 'Powder', 'Suspension', 'Other'].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Method Reference</label>
            <input
              className="input"
              value={form.methodRef}
              onChange={(e) => setForm((f) => ({ ...f, methodRef: e.target.value }))}
              placeholder="e.g. USP-2024-AMX"
            />
          </div>
          <div className="col-span-2">
            <label className="label">Description</label>
            <textarea
              className="input resize-none"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Specification description and scope..."
            />
          </div>
        </div>
      </Modal>

      {/* Add Parameter Modal */}
      <Modal
        isOpen={showAddParam}
        onClose={() => setShowAddParam(false)}
        title="Add Parameter"
        subtitle={selectedSpec ? `Adding to: ${selectedSpec.product}` : ''}
        size="md"
        footer={
          <>
            <button onClick={() => setShowAddParam(false)} className="btn-secondary">Cancel</button>
            <button
              onClick={() => {
                if (selectedSpec) {
                  addParamMutation.mutate({ specId: selectedSpec.id, param: paramForm });
                }
              }}
              disabled={addParamMutation.isPending || !paramForm.name}
              className="btn-primary"
            >
              {addParamMutation.isPending ? 'Adding...' : 'Add Parameter'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Parameter Name *</label>
            <input
              className="input"
              value={paramForm.name}
              onChange={(e) => setParamForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Assay, Dissolution, Water Content"
            />
          </div>
          <div>
            <label className="label">Unit</label>
            <input
              className="input"
              value={paramForm.unit}
              onChange={(e) => setParamForm((f) => ({ ...f, unit: e.target.value }))}
              placeholder="e.g. %, mg, kP"
            />
          </div>
          <div>
            <label className="label">Target Value</label>
            <input
              type="number"
              className="input"
              value={paramForm.targetValue}
              onChange={(e) => setParamForm((f) => ({ ...f, targetValue: e.target.value }))}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="label">Min Value</label>
            <input
              type="number"
              className="input"
              value={paramForm.minValue}
              onChange={(e) => setParamForm((f) => ({ ...f, minValue: e.target.value }))}
              placeholder="Leave blank if NLT not applicable"
            />
          </div>
          <div>
            <label className="label">Max Value</label>
            <input
              type="number"
              className="input"
              value={paramForm.maxValue}
              onChange={(e) => setParamForm((f) => ({ ...f, maxValue: e.target.value }))}
              placeholder="Leave blank if NMT not applicable"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
